import { X } from "lucide-react";
import type {
  FilterRow,
  FilterableLabel,
  FilterOperator,
} from "./functional-graph-filter-panel";

interface FunctionalGraphFilterChipsProps {
  filters: FilterRow[];
  onRemove: (id: string) => void;
  onClearAll: () => void;
}

// BREEZEAI-1054: label display names (Task is a UI-facing rename of Outcome).
const LABEL_DISPLAY: Record<FilterableLabel, string> = {
  Persona: "Persona",
  Outcome: "Task",
  Scenario: "Scenario",
  Step: "Step",
  Action: "Action",
  Api: "Api",
};

// Colours mirror the graph's node legend so a filter chip and its target node
// type read as the same thing across surfaces.
const LABEL_ACCENT: Record<
  FilterableLabel,
  { dot: string; bg: string; text: string; border: string }
> = {
  Persona: {
    dot: "#FFB3E6",
    bg: "bg-[#FFB3E6]/15",
    text: "text-[#FFB3E6]",
    border: "border-[#FFB3E6]/40",
  },
  Outcome: {
    dot: "#A4DD00",
    bg: "bg-[#A4DD00]/15",
    text: "text-[#A4DD00]",
    border: "border-[#A4DD00]/40",
  },
  Scenario: {
    dot: "#569480",
    bg: "bg-[#569480]/20",
    text: "text-[#7FB8A2]",
    border: "border-[#569480]/50",
  },
  Step: {
    dot: "#AD7BE9",
    bg: "bg-[#AD7BE9]/15",
    text: "text-[#C29CEF]",
    border: "border-[#AD7BE9]/40",
  },
  Action: {
    dot: "#FB8500",
    bg: "bg-[#FB8500]/15",
    text: "text-[#FFA333]",
    border: "border-[#FB8500]/40",
  },
  Api: {
    dot: "#4C8EDA",
    bg: "bg-[#4C8EDA]/15",
    text: "text-[#78AEE3]",
    border: "border-[#4C8EDA]/40",
  },
};

const OPERATOR_DISPLAY: Record<FilterOperator, string> = {
  $eq: "=",
  $contains: "contains",
  $containsi: "contains",
  $in: "in",
  $startsWith: "starts with",
  $startsWithi: "starts with",
  $endsWith: "ends with",
  $endsWithi: "ends with",
  $regex: "matches",
  $regexi: "matches",
};

// Attribute keys → readable labels. Falls back to the raw key so any future
// attribute still renders correctly without needing to touch this map.
const ATTRIBUTE_DISPLAY: Record<string, string> = {
  persona: "name",
  outcome: "name",
  scenario: "name",
  step: "name",
  action: "name",
  method: "method",
  url: "URL",
  description: "description",
  nameKey: "name key",
  embedText: "embed text",
  citationRefs: "citations",
  order: "order",
};

export default function FunctionalGraphFilterChips({
  filters,
  onRemove,
  onClearAll,
}: FunctionalGraphFilterChipsProps) {
  if (filters.length === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-2">
      {filters.map((row) => {
        const accent = LABEL_ACCENT[row.label];
        const attrLabel = ATTRIBUTE_DISPLAY[row.attribute] ?? row.attribute;
        return (
          <div
            key={row.id}
            className={`group inline-flex items-stretch overflow-hidden rounded-full border ${accent.border} bg-surface-card shadow-sm transition-colors`}
            title={`${LABEL_DISPLAY[row.label]} · ${attrLabel} ${OPERATOR_DISPLAY[row.operator]} "${row.value}"`}
          >
            {/* Label pill — colour-coded to the graph legend so the chip and
                the node it filters read as the same thing. */}
            <div
              className={`flex items-center gap-1.5 pl-2.5 pr-2 py-1 ${accent.bg} ${accent.text}`}
            >
              <span
                aria-hidden
                className="inline-block h-1.5 w-1.5 rounded-full"
                style={{ backgroundColor: accent.dot }}
              />
              <span className="text-[11px] font-semibold uppercase tracking-wide">
                {LABEL_DISPLAY[row.label]}
              </span>
            </div>

            {/* Predicate — muted attribute + operator, then the value in bold
                so the eye lands on what the user actually typed. */}
            <div className="flex items-center gap-1.5 px-2.5 py-1 text-xs text-text-primary">
              <span className="text-muted">{attrLabel}</span>
              <span className="text-muted">{OPERATOR_DISPLAY[row.operator]}</span>
              <span className="font-semibold text-white truncate max-w-[10rem]">
                {row.value}
              </span>
            </div>

            {/* Dismiss — separate hit target with its own hover so the chip
                itself doesn't feel like it might be clickable. */}
            <button
              type="button"
              onClick={() => onRemove(row.id)}
              className="flex items-center justify-center px-2 border-l border-surface-border text-muted hover:bg-red-500/15 hover:text-red-400 transition-colors"
              aria-label={`Remove ${LABEL_DISPLAY[row.label]} filter`}
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        );
      })}

      {filters.length > 1 && (
        <button
          type="button"
          onClick={onClearAll}
          className="ml-1 inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium text-muted hover:text-white hover:bg-surface-hover transition-colors"
        >
          <X className="h-3 w-3" />
          Clear all
        </button>
      )}
    </div>
  );
}
